import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { axios, Fetcher, MutationFetcher, SWRKey, useSWRImmutable, useSWRMutation } from 'services/swr';
import { useUser } from 'services/User';
import { ScanResultCode } from 'types/Workflows';
import { getScanResultLabel } from 'utils/verdict/Verdict';

export enum DeliveryMethod {
    Email = 'email',
    InApp = 'inApp',
}

export enum UserTag {
    UserAdded = 'UserAdded',
    UserRemoved = 'UserRemoved',
    UserRoleChanged = 'UserRoleChanged',
    IPWhitelistRestriction = 'IPWhitelistRestriction',
}

export enum PlatformTag {
    FileScanThreshold = 'FileScanThreshold',
    ScanResultCode = 'ScanResultCode',
}

export const useTagLabel = () => {
    const { t } = useTranslation();

    const getTagLabel = useCallback(
        (tag: UserTag | PlatformTag) => {
            switch (tag) {
                case UserTag.UserAdded:
                    return t('User Added to Organization');
                case UserTag.UserRemoved:
                    return t('User Removed from Organization');
                case UserTag.UserRoleChanged:
                    return t("User's Role Changed in the Organization");
                case UserTag.IPWhitelistRestriction:
                    return t('Request IP Not in Allowlisted');
                case PlatformTag.FileScanThreshold:
                    return t('File Scan Threshold Reached');
                case PlatformTag.ScanResultCode:
                    return t('Scan Status Detected');
                default:
                    return '-';
            }
        },
        [t],
    );

    return { getTagLabel };
};

export const useTagDescription = () => {
    const { t } = useTranslation();

    const getTagDescription = (tag: UserTag | PlatformTag) => {
        switch (tag) {
            case UserTag.UserAdded:
                return t('User added to the Organization');
            case UserTag.UserRemoved:
                return t('User removed to the Organization');
            case UserTag.UserRoleChanged:
                return t("User's role changed in the Organization");
            case UserTag.IPWhitelistRestriction:
                return t('Access denied for request outside the allowlisted IP range');
            case PlatformTag.FileScanThreshold:
                return t('File scan threshold reached');
            case PlatformTag.ScanResultCode:
                return t('Scan status detected');
            default:
                return '-';
        }
    };

    return { getTagDescription };
};

export const getScanResultCodeOptions = () => {
    // Supported codes from __config.ENGINE_STATUS (Platform API)
    const supportedCodes = [
        ScanResultCode.Infected, // INFECTED -> 1
        ScanResultCode.Suspicious, // SUSPICIOUS -> 2
        ScanResultCode.Failed, // FAILED_TO_SCAN -> 3
        ScanResultCode.Whitelisted, // SKIPPED_CLEAN -> 7
        ScanResultCode.Blacklisted, // SKIPPED_DIRTY -> 8
        ScanResultCode.ExceededArchiveDepth, // EXCEEDED_ARCHIVE_DEPTH -> 9
        ScanResultCode.NotScanned, // NOT_SCANNED -> 10
        ScanResultCode.EncryptedArchive, // ENCRYPTED_ARCHIVE -> 12
        ScanResultCode.ExceededArchiveSize, // EXCEEDED_ARCHIVE_SIZE -> 13
        ScanResultCode.ExceededArchiveFileNumber, // EXCEEDED_ARCHIVE_FILE_NUMBER -> 14
        ScanResultCode.PasswordProtectedDocument, // PASSWORD_PROTECTED_DOCUMENT -> 15
        ScanResultCode.ExceededArchiveTimeout, // EXCEEDED_ARCHIVE_TIMEOUT -> 16
        ScanResultCode.Mismatch, // FILE_TYPE_MISMATCH -> 17
        ScanResultCode.PotentiallyVulnerableFile, // POTENTIALLY_VULNERABLE_FILE -> 18
        ScanResultCode.Cancelled, // CANCELED -> 19
        ScanResultCode.SensitiveDataFound, // SENSITIVE_DATA_FOUND -> 20
        ScanResultCode.YaraRuleMatched, // YARA_RULE_MATCHED -> 21
        ScanResultCode.PotentiallyUnwanted, // POTENTIALLY_UNWANTED_PROGRAM -> 22
        ScanResultCode.UnsupportedFileType, // UNSUPPORTED_FILE_TYPE -> 23
        ScanResultCode.ExtractionFailed, // EXTRACTION_FAILED -> 24
        ScanResultCode.ScanFailed, // SCAN_FAILED -> 25
        ScanResultCode.BlockedDeepCDR, // BLOCKED_BY_CDR -> 30
        ScanResultCode.GlobalTimeoutExceeded, // GLOBAL_TIMEOUT_EXCEEDED -> 31
        ScanResultCode.AllowedCOO, // ALLOWED_BY_COO -> 41
        ScanResultCode.BlockedCOO, // BLOCKED_BY_COO -> 42
    ];
    const options = supportedCodes.map((code) => ({
        value: code.toString(),
        label: getScanResultLabel(code),
    }));

    return options;
};

export enum LimitInterval {
    Monthly = 'monthly',
    Daily = 'daily',
}

export enum LimitAPI {
    Prevention = 'Prevention',
    Reputation = 'Reputation',
    Sandbox = 'Sandbox',
    Download = 'Download',
}

export enum LimitPercentage {
    Fifty = '50%',
    Sixty = '60%',
    Seventy = '70%',
    Eighty = '80%',
    Ninety = '90%',
    Hundred = '100%',
}

export type LimitTag = `${Capitalize<LimitInterval>}Limit-${LimitAPI}API-${LimitPercentage}`;

export interface SubscribedTag {
    tag?: UserTag | PlatformTag | LimitTag;
    description?: string;
    delivery?: DeliveryMethod[];
}

export const useGetSubscribedTags = () => {
    const { user } = useUser();
    const organizationId = user?.organization?.organization_id;
    const userId = user?.sso_user_id;

    const cacheKey: SWRKey = useMemo(() => {
        if (organizationId && userId) {
            return {
                url: '/api/organization/get-subscribed-tags',
                baseURL: process.env.GATSBY_SITE_URL,
                data: {
                    organizationId,
                    userId,
                },
            };
        }

        return null;
    }, [organizationId, userId]);

    const fetcher: Fetcher<Array<SubscribedTag>, SWRKey> = async ({ url, data, ...config }) =>
        axios.post(url, data, config).then((res) => res.data);

    return useSWRImmutable(cacheKey, fetcher);
};

export const useSetSubscribedTags = () => {
    const { user } = useUser();
    const organizationId = user?.organization?.organization_id;
    const userId = user?.sso_user_id;

    const cacheKey: SWRKey = {
        url: '/api/organization/set-subscribed-tags',
        baseURL: process.env.GATSBY_SITE_URL,
        data: {
            organizationId,
            userId,
        },
    };

    const fetcher: MutationFetcher<{ success: boolean }, SWRKey, Array<SubscribedTag>> = async (
        { url, data, ...config },
        { arg: body },
    ) => {
        const payload = {
            ...data,
            body,
        };

        return axios.post(url, payload, config).then((res) => res.data);
    };

    return useSWRMutation(cacheKey, fetcher, { revalidate: false });
};
